import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  Stack,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  People,
  Description,
  Security,
  ArrowForward,
  CheckCircle,
  Star,
  Business,
  Analytics,
  CloudDone,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const features = [
    {
      icon: <People sx={{ fontSize: 40 }} />,
      title: 'Lead Management',
      description: 'Track and nurture leads with our intuitive CRM tools',
      color: '#667eea',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: 'Opportunity Tracking',
      description: 'Convert leads to opportunities and close deals faster',
      color: '#764ba2',
    },
    {
      icon: <Description sx={{ fontSize: 40 }} />,
      title: 'Contract Management',
      description: 'Access and manage all your contracts in one place',
      color: '#f59e0b',
    },
    {
      icon: <Analytics sx={{ fontSize: 40 }} />,
      title: 'Real-time Analytics',
      description: 'Get insights into your sales performance and metrics',
      color: '#10b981',
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: 'Secure & Compliant',
      description: 'Enterprise-grade security with SOX, HIPAA compliance',
      color: '#ef4444',
    },
    {
      icon: <CloudDone sx={{ fontSize: 40 }} />,
      title: 'Cloud-Based',
      description: 'Access your data anywhere, anytime, on any device',
      color: '#3b82f6',
    },
  ];

  const benefits = [
    'Increase sales productivity by 40%',
    'Reduce lead response time by 60%',
    'Improve conversion rates by 25%',
    'Access from anywhere, anytime',
    'Real-time collaboration',
    '24/7 customer support',
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: '#fafbfc' }}>
      {/* Navigation Header */}
      <Box
        component="nav"
        sx={{
          position: 'sticky',
          top: 0,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          zIndex: 1000,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Business sx={{ fontSize: 32, color: theme.palette.primary.main }} />
              <Typography variant="h6" fontWeight="bold">
                Tripl-i Partner Portal
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
              }}
            >
              Sign In
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
          color: 'white',
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Chip
                  label="🚀 Partner Success Platform"
                  sx={{
                    mb: 3,
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 500,
                  }}
                />
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 800,
                    mb: 3,
                    lineHeight: 1.2,
                  }}
                >
                  Grow Your Business with Tripl-i
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 4,
                    opacity: 0.95,
                    fontWeight: 400,
                    lineHeight: 1.6,
                  }}
                >
                  Empower your sales team with our comprehensive partner portal.
                  Manage leads, track opportunities, and close deals faster than ever.
                </Typography>
                <Button
                  size="large"
                  variant="contained"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/login')}
                  sx={{
                    background: 'white',
                    color: theme.palette.primary.main,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.9)',
                    },
                  }}
                >
                  Sign In to Portal
                </Button>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: 300, md: 400 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    <Stack spacing={3} sx={{ p: 4 }}>
                      {['Lead Captured', 'Opportunity Created', 'Deal Closed'].map((text, index) => (
                        <motion.div
                          key={text}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 + index * 0.2 }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              p: 2,
                              background: 'rgba(255, 255, 255, 0.15)',
                              borderRadius: 2,
                            }}
                          >
                            <CheckCircle sx={{ color: '#10b981' }} />
                            <Typography variant="h6">{text}</Typography>
                          </Box>
                        </motion.div>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 2,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Everything You Need to Succeed
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}
          >
            Our partner portal provides all the tools and features you need to manage
            your business relationships effectively
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card
                  sx={{
                    height: '100%',
                    transition: 'transform 0.3s',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${feature.color}20, ${feature.color}10)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        color: feature.color,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom fontWeight="600">
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ background: 'linear-gradient(135deg, #f3f4f6 0%, #ffffff 100%)', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Typography variant="h3" sx={{ mb: 4, fontWeight: 700 }}>
                  Why Choose Tripl-i?
                </Typography>
                <Stack spacing={3}>
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CheckCircle sx={{ color: theme.palette.success.main }} />
                        <Typography variant="h6" fontWeight="400">
                          {benefit}
                        </Typography>
                      </Box>
                    </motion.div>
                  ))}
                </Stack>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    background: 'white',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} sx={{ color: '#f59e0b', fontSize: 24 }} />
                    ))}
                    <Typography variant="body1" sx={{ ml: 1 }}>
                      4.9/5 Rating
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ mb: 2, fontStyle: 'italic' }}>
                    "Tripl-i has transformed how we manage our partner relationships.
                    The portal is intuitive, powerful, and has helped us increase our
                    sales by 45% in just 6 months."
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    — Sarah Johnson, VP of Sales at TechCorp
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
          color: 'white',
          py: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Typography variant="h3" align="center" sx={{ mb: 3, fontWeight: 700 }}>
              Ready to Get Started?
            </Typography>
            <Typography variant="h6" align="center" sx={{ mb: 4, opacity: 0.95 }}>
              Join thousands of partners who are already growing their business with Tripl-i
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                size="large"
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{
                  background: 'white',
                  color: theme.palette.primary.main,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.9)',
                  },
                }}
              >
                Sign In
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ background: '#1f2937', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              © 2025 Tripl-i. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={3}>
              <Typography
                variant="body2"
                component="a"
                href="#"
                sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
              >
                Privacy Policy
              </Typography>
              <Typography
                variant="body2"
                component="a"
                href="#"
                sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
              >
                Terms of Service
              </Typography>
              <Typography
                variant="body2"
                component="a"
                href="#"
                sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
              >
                Contact Support
              </Typography>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;